/****************************************************************
 * Copyright (C) 2015-2020, 2023 VMware, Inc. All rights reserved.
 ****************************************************************/
/**
 * @fileoverview rtavsession.js -- RtavSession
 * Service to handle wmks session management
 *
 * Class that holds channel, setting, as well as status. It depends on RtavSessionController, rtavProtocolService,
 * and also depends on local class RtavChannel, RtavSettingManager, RtavStatus
 */

import RTAV from './logservice';
import { clientUtil } from '@html-core';

/**
 * Class that contains functions and logic for using the rtav sub channel
 * @type {function}
 */
let RtavChannel = function(vvcSession) {
   const RTAV_H264_CODEC = 0x0008;
   const RTAV_OPUS_CODEC = 0x0020;
   const RTAV_H264_OPUS_CODEC = 0x0028;

   let rtavChannel = null;

   return {
      initialize: function() {
         //halt the sending and recieving if any thing goes wrong
         let onChannelCloseError;

         if (!vvcSession) {
            return;
         }
         let initialDataArr = null;
         RTAV.logService.debug("vvcSession.enableRTAVH264Codec in rtavsession is " + vvcSession.enableRTAVH264Codec);
         RTAV.logService.debug("vvcSession.enableRTAVOpusCodec in rtavsession is " + vvcSession.enableRTAVOpusCodec);
         if (vvcSession.enableRTAVH264Codec !== false && vvcSession.enableRTAVOpusCodec !== false) {
            initialDataArr = clientUtil.pushBytes(RTAV_H264_OPUS_CODEC, 4, false);
         } else if (vvcSession.enableRTAVH264Codec !== false && vvcSession.enableRTAVOpusCodec === false) {
            initialDataArr = clientUtil.pushBytes(RTAV_H264_CODEC, 4, false);
         }
         rtavChannel = vvcSession.openChannel('MultimediaRedirChannel', 10,
            null, null, initialDataArr);
         rtavChannel.onopen = function() {
            RTAV.logService.log('rtav channel is opened');
         };
         onChannelCloseError = function() {
            RTAV.logService.log('rtav channel is closed');
            this.stop();
         }.bind(this);
         rtavChannel.onclose = onChannelCloseError;

         rtavChannel.onerror = onChannelCloseError;
         this.send = function(networkBytes) {
            if (!networkBytes) {
               return;
            }
            try {
               vvcSession.send(rtavChannel, networkBytes);
            } catch (e) {
               RTAV.logService.log('error when sending rtav message:' + e);
            }
         };
      },

      send: function() {},

      stop: function() {
         if (!rtavChannel) {
            return;
         }
         this.send = function() {
            RTAV.logService.debug('stop one package sending for rtav channel is closed');
         };
         rtavChannel.onmessage = function() {
            RTAV.logService.debug('stop one package recieving for rtav channel is closed');
         };
         rtavChannel = null;
      },

      setReceivedListener: function(onMessage) {
         if (!rtavChannel) {
            return;
         }
         rtavChannel.onmessage = onMessage;
      },

      rtavChannel
   };
};

/**
 * Class that contains functions and logic for managing and formating RTAV configurable settings
 * @type {function}
 */
let RtavSettingManager = function() {
   let adminPolicy,
      localConfig,
      //private function
      setLocalConfig;

   // setting the local config with admin policy and ingore local settings since we avoid provide user UI for that.
   setLocalConfig = function(adminPolicy) {
      let localWidth,
         localHeight,
         localMaxFPS;

      if (adminPolicy.vdoDefResWidth !== 0) {
         // client set has no limit, use agent default value
         localWidth = adminPolicy.vdoDefResWidth;
      } else {
         if (adminPolicy.vdoMaxResWidth !== 0) {
            localWidth = Math.min(adminPolicy.vdoMaxResWidth, RTAV.settings.video.defResWidth);
         } else {
            localWidth = RTAV.settings.video.defResWidth;
         }
      }
      if (adminPolicy.vdoDefResHeight !== 0) {
         // client set has no limit, use agent default value
         localHeight = adminPolicy.vdoDefResHeight;
      } else {
         if (adminPolicy.vdoMaxResHeight !== 0) {
            localHeight = Math.min(adminPolicy.vdoMaxResHeight, RTAV.settings.video.defResHeight);
         } else {
            localHeight = RTAV.settings.video.defResHeight;
         }
      }
      if (adminPolicy.vdoMaxFPS !== 0) {
         if (RTAV.settings.video.defMaxFPS !== 0) {
            localMaxFPS = Math.min(RTAV.settings.video.defMaxFPS, adminPolicy.vdoMaxFPS);
         } else {
            localMaxFPS = adminPolicy.vdoMaxFPS;
         }
      } else {
         localMaxFPS = RTAV.settings.video.defMaxFPS;
      }

      // round the resolution since cpmpression lib suggest so.
      if (localWidth % 16 !== 0) {
         localWidth = Math.floor(localWidth / 16) * 16;
      }
      if (localHeight % 16 !== 0) {
         localHeight = Math.floor(localHeight / 16) * 16;
      }

      localConfig = {
         versionNum: adminPolicy.versionNum,
         isEnabled: adminPolicy.isEnabled,
         vdoRes: {
            width: localWidth,
            height: localHeight
         },
         vdoFPS: localMaxFPS,
         reserved: 0
      };
   };
   return {
      setAdminPolicy: function(policy) {
         adminPolicy = policy;
         setLocalConfig(adminPolicy);
      },

      getLocalConfig: function() {
         return localConfig;
      },

      getAudioDeviceSetting: function() {
         return RTAV.settings.audio;
      },

      getVideoDeviceSetting: function() {
         return {
            width: localConfig.vdoRes.width,
            height: localConfig.vdoRes.height,
            fps: localConfig.vdoFPS
         };
      }
   };
};

/**
 * Class that contains functions and logic for managing and formating RTAV configurable settings
 * @type {function}
 * It hasn't been fully implemented yet
 */
let RtavStatus = function() {
   let currentStatus,
      setStatus,
      AVPluginState = RTAV.constants.AVPluginState;

   setStatus = function(statusName) {
      if (!AVPluginState.hasOwnProperty(statusName)) {
         RTAV.logService.error('bad statusName to be set for rtav session');
      }
      currentStatus = AVPluginState[statusName];
      RTAV.logService.debug("status changed to " + statusName);
   };
   currentStatus = setStatus('STChannelClose');

   return {
      set: setStatus,

      processAllowed: function(actionName) {
         return true;
      }
   };
};

/**
 * The Class that contains all information for a rtav session.
 * @param {string} sessionId  The ID of vvcSession
 * @param {object} vvcSession The vvcSession object passed from wmks, with send and openChannel API.
 *                            Is null in seamless window mode.
 */
export default function RtavSession(sessionId, vvcSession) {
   let channel,
      settingManager,
      status,
      controller,
      protocol,
      deviceMessageManager,
      // private function
      onMessage,
      sendStopStream,
      getRTAVCodec,
      isAgentSupportAudioDTX;


   settingManager = new RtavSettingManager();

   channel = new RtavChannel(vvcSession);

   status = new RtavStatus();
   controller = new RTAV.RtavSessionController(sessionId, vvcSession.hardwareAccelerationOption);
   protocol = RTAV.rtavProtocolService;
   deviceMessageManager = new RTAV.DeviceMessageManager(controller);
   controller.setMessageManager(deviceMessageManager);

   getRTAVCodec = function(parsedMessage) {
      let localConfig;
      let enableRTAVH264CodecKillSwitch = true; // default video codec is H.264
      let enableRTAVOpusCodecKillSwitch = true; // default audio codec is Opus
      if (vvcSession.enableRTAVH264Codec === true || vvcSession.enableRTAVH264Codec === false) {
         enableRTAVH264CodecKillSwitch = vvcSession.enableRTAVH264Codec;
      }
      if (vvcSession.enableRTAVOpusCodec === true || vvcSession.enableRTAVOpusCodec === false) {
         enableRTAVOpusCodecKillSwitch = vvcSession.enableRTAVOpusCodec;
      }
      RTAV.logService.debug("h264 encode kill switch value is " + enableRTAVH264CodecKillSwitch);
      RTAV.logService.debug("opus encode kill switch value is " + enableRTAVOpusCodecKillSwitch);
      localConfig = settingManager.getLocalConfig();
      if (enableRTAVH264CodecKillSwitch === true
         && enableRTAVOpusCodecKillSwitch === true
         && parsedMessage.data.codecPrefs === RTAV.constants.codecTypes['CodecVmwH264Opus'] ) {
         /**
          * In this situation, if it's web client the browser is chrome/Edge or it's Chrome Client,
          * both clients can support WebCodecs API and Opus, which means client codecs preferences
          * is H264Opus and client received agent side support codec
          * H264Opus, then after negotiation, codec preference should be
          * H264Opus.
          */
         localConfig.codecPref = RTAV.constants.codecTypes['CodecVmwH264Opus'];
         RTAV.logService.log("after negotiation, final codec type is CodecVmwH264Opus");
      } else if (enableRTAVH264CodecKillSwitch === true
         && enableRTAVOpusCodecKillSwitch === false
         && parsedMessage.data.codecPrefs === RTAV.constants.codecTypes['CodecVmwH264Opus']) {
         /**
          * In this situation, if it's web client, the browser is chrome/Edge or it's Chrome Client,
          * both client can support WebCodecs API but not Opus, which means client codecs preferences
          * is H264Speex and client received agent side support codec H264Opus, then after negotiation,
          * codec preference should be H264Speex.
          */
         localConfig.codecPref = RTAV.constants.codecTypes['CodecVmwH264Speex'];
         RTAV.logService.log("after negotiation, final codec type is CodecVmwH264Speex");
      } else if (enableRTAVH264CodecKillSwitch === true
         && parsedMessage.data.codecPrefs === RTAV.constants.codecTypes['CodecVmwH264Speex']) {
         /**
          * In this situation, if it's web client, the browser is chrome/Edge or it's Chrome Client,
          * both client can support WebCodecs API, which means client codecs preferences
          * is H264 but not sure whether support Opus and client received agent side support codec
          * H264Speex, then after negotiation, codec preference should be
          * H264Speex.
          */
         localConfig.codecPref = RTAV.constants.codecTypes['CodecVmwH264Speex'];
         RTAV.logService.log("after negotiation, final codec type is CodecVmwH264Speex");
      } else {
         localConfig.codecPref = RTAV.constants.codecTypes['CodecVmwTheoraSpeex'];
         RTAV.logService.log("after negotiation, final codec type is CodecVmwTheoraSpeex");
         if (!window.VideoEncoder || !window.AudioEncoder) {
            RTAV.logService.debug("this browser do not support API webCodecs");
         }
      }
      return localConfig.codecPref;
   };

   isAgentSupportAudioDTX = function() {
      let localConfig;
      localConfig = settingManager.getLocalConfig();
      if ((localConfig.versionNum > 0x00000001 && localConfig.versionNum < 0x02000000) || localConfig.versionNum > 0x02000000) {
         // If agent (version > 0.0.1 && version < 2.0.0) or version > 2.0.0
         return true;
      } else {
         return false;
      }
   };

   onMessage = function(message) {
      let parsedMessage = protocol.parseRecievedMessage(message),
         adminPolicy,
         localConfig,
         deviceConfig,
         rtavMessageTypes = RTAV.constants.rtavTypes,
         success,
         binaryData,
         finalCodecPref;

      if (!parsedMessage) {
         RTAV.logService.warn('skip processing for unknown message');
         return;
      }
      switch (parsedMessage.type) {
         case rtavMessageTypes['PMsgGetConfig']:
            // set admin policy, and set user config back to the Agent
            adminPolicy = parsedMessage.data;
            settingManager.setAdminPolicy(adminPolicy);
            localConfig = settingManager.getLocalConfig();
            binaryData = protocol.getSendingBinary('sendConfig', localConfig);
            controller.setAudioDTXMode(vvcSession.enableRTAVDTX ? isAgentSupportAudioDTX() : false);
            channel.send(binaryData);
            break;
         case rtavMessageTypes['PMsgGetCliSettings']:
            finalCodecPref = getRTAVCodec(parsedMessage);
            deviceConfig = {
               audio: settingManager.getAudioDeviceSetting(),
               video: settingManager.getVideoDeviceSetting(),
               codecPref: finalCodecPref
            };
            binaryData = protocol.getSendingBinary('sendClientSettings', deviceConfig);
            channel.send(binaryData);
            break;
         case rtavMessageTypes['PMsgStart_A']:
            deviceMessageManager.onMessage('PMsgStart_A', function(success) {
               if (success) {
                  binaryData = protocol.getSendingBinary('sendStartAudioAck');
               } else {
                  binaryData = protocol.getSendingBinary('sendStartAudioNak');
               }
               channel.send(binaryData);
            });
            break;
         case rtavMessageTypes['PMsgStop_A']:
            deviceMessageManager.onMessage('PMsgStop_A', function(success) {
               if (success) {
                  binaryData = protocol.getSendingBinary('sendStopAudioAck');
               } else {
                  binaryData = protocol.getSendingBinary('sendStopAudioNak');
               }
               channel.send(binaryData);

            });
            break;
         case rtavMessageTypes['PMsgStart_V']:
            finalCodecPref = getRTAVCodec(parsedMessage);
            deviceMessageManager.onMessage('PMsgStart_V', function(success) {
               if (success) {
                  deviceConfig = {
                     audio: settingManager.getAudioDeviceSetting(),
                     video: settingManager.getVideoDeviceSetting(),
                     codecPref: finalCodecPref
                  };
                  binaryData = protocol.getSendingBinary('sendStartVideoAck', deviceConfig);
               } else {
                  binaryData = protocol.getSendingBinary('sendStartVideoNak');
               }
               channel.send(binaryData);
            });
            break;
         case rtavMessageTypes['PMsgStop_V']:
            deviceMessageManager.onMessage('PMsgStop_V', function(success) {
               if (success) {
                  binaryData = protocol.getSendingBinary('sendStopVideoAck');
               } else {
                  binaryData = protocol.getSendingBinary('sendStopVideoNak');
               }
               channel.send(binaryData);
            });
            break;
         case rtavMessageTypes['PMsgStartStream']:
            localConfig = settingManager.getLocalConfig();
            deviceMessageManager.onMessage("PMsgStartStream", function(data) {
               let binaryStream = protocol.getSendingBinary('sendStream', data);
               channel.send(binaryStream);
            }, {
               audio: settingManager.getAudioDeviceSetting(),
               video: settingManager.getVideoDeviceSetting(),
               codecPref: localConfig.codecPref
            });
            break;
         case rtavMessageTypes['PMsgStopStream']:
            controller.stopMediaIn();
            break;
         default:
            RTAV.logService.error('unknown msg');
            break;
      }
   };

   sendStopStream = function() {
      let binaryData;
      binaryData = protocol.getSendingBinary('sendStopStream');
      channel.send(binaryData);
   };

   return {
      /**
       * Will always start the rtav session, but might fail to get device and thus session has no data to send.
       */
      start: function() {
         RTAV.logService.debug("start RTAV");
         channel.initialize();
         channel.setReceivedListener(onMessage);
         controller.initialize();
         status.set('STChannelOpen');
      },

      /**
       * Stop the whole rtav, will clear all resources and disconnect from agent.
       */
      stop: function() {
         RTAV.logService.debug("stop RTAV by force");
         channel.stop();
         controller.stop();
         status.set('STChannelClose');
      },

      /**
       * User want to stop the rtav from working for now, but several resources will be kept for later usage.
       */
      userStop: function() {
         RTAV.logService.debug("stop RTAV by userAction");
         controller.userStop();
         sendStopStream();
         status.set('STOff');
      },

      /**
       * [isUsingDevices description]
       * @param  {string}  type Optional Indicate whether asking for audio or video, when undefined, ask for either
       * @return {boolean} This returns whether the asked type is in usage
       */
      isUsingDevices: function(type) {
         return controller.isSendingStream(type);
      },

      /**
       * [isUsingDevices description]
       * @param  {string}  type Optional Indicate whether asking for audio or video, when undefined, ask for either
       * @return {boolean} This returns whether the asked type is being asked for permission
       */
      isAskingPremission: function(type) {
         return deviceMessageManager.isPermissionPending(type);
      },

      channel,
      controller,
      status,
      onMessage
   };
}